/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a step or trigger code; it renders nothing */
// C1b Sync scheduler for the web: ONE scheduler owns every drain and
// refresh (the C4 queue, the deck outbox, the deck and collection
// snapshots). Queues and refreshers never schedule themselves; they call
// requestSync(trigger).
//
// Triggers: recovered (C1), session_restored (C2), app_start, foreground,
// enqueued, and a 60 s tick while work or a session refresh is pending.
// Run order: step 0 the single-flight session refresh (C1 online, C2
// refresh_pending, its own backoff elapsed; only `recovered` makes it due
// at once), then the account steps (C1 online AND C2 active), then the
// public steps (C1 online). Single flight with at most one rerun.
import { CONN, MODE, TRIGGER, type ConnectivityState, type IdentityMode, type SyncTrigger } from './offline.codes'
import { SchedulerCore, TICK_MS, type RunPlan } from './schedulerCore'

export interface SchedulerDeps {
  connectivity: () => ConnectivityState
  mode: () => IdentityMode
  /** Any due drain or snapshot work (records, dirty drafts, a stale snapshot). Synchronous (a cached answer). */
  hasPendingWork: () => boolean
  /** Step 0: one single-flight session refresh. */
  refreshSession: () => Promise<'ok' | 'transient' | 'terminal'>
  /** recovered / session_restored: every waiting head due now. */
  makeHeadsDue: () => Promise<void>
  now: () => number
  jitter: () => number
}

export interface SyncStep {
  name: string
  /** account: C1 online AND C2 active. public: C1 online. */
  kind: 'account' | 'public'
  run: () => Promise<void>
}

let core = new SchedulerCore()
let deps: SchedulerDeps | null = null
let steps: SyncStep[] = []
let tickTimer: ReturnType<typeof setInterval> | null = null
let refreshingInRun = false
let running: Promise<void> | null = null
const runListeners = new Set<() => void>()
/** Tests: every run's plan, in order. */
export const planLog: RunPlan[] = []
/** Web Lock serializing the account steps across this browser's tabs. */
export const SYNC_LOCK = 'cb-offline-sync'

export function configureScheduler(d: SchedulerDeps, s: SyncStep[] = []): void {
  deps = d
  steps = [...s]
}

export function addSchedulerStep(step: SyncStep): void {
  steps = [...steps.filter(x => x.name !== step.name), step]
}

export function isSyncRunning(): boolean {
  return core.running
}

export function onSyncRunEnd(fn: () => void): () => void {
  runListeners.add(fn)
  return () => { runListeners.delete(fn) }
}

/** Ask for a run. Cheap; safe to call from anywhere. */
export function requestSync(trigger: SyncTrigger): void {
  const d = deps
  if (!d) return
  if (trigger === TRIGGER.sessionRestored && refreshingInRun) return
  const refreshPending = d.mode() === MODE.refreshPending
  if (!SchedulerCore.admits(trigger, d.hasPendingWork(), refreshPending)) return
  const plan = core.begin(trigger, d.now(), d.connectivity() === CONN.online, d.mode())
  if (trigger === TRIGGER.recovered || trigger === TRIGGER.sessionRestored) void d.makeHeadsDue().catch(() => {})
  if (!plan) return
  running = execute(plan, d)
}

/** Resolves when the current run (and its rerun) finished (tests, sign-out). */
export async function whenSyncIdle(): Promise<void> {
  for (let i = 0; i < 10 && running; i++) await running
}

async function execute(plan: RunPlan, d: SchedulerDeps): Promise<void> {
  planLog.push(plan)
  let drain = plan.drain
  try {
    if (plan.attemptRefresh) {
      refreshingInRun = true
      let result: 'ok' | 'transient' | 'terminal' = 'transient'
      try {
        result = await d.refreshSession()
      } catch {
        result = 'transient'
      }
      refreshingInRun = false
      core.refreshFinished(result === 'ok', d.now(), d.jitter())
      if (result === 'ok') {
        // The same run continues with the drain.
        drain = d.connectivity() === CONN.online && d.mode() === MODE.active
        if (drain) await d.makeHeadsDue().catch(() => {})
      }
    }
    const runSteps = async () => {
      for (const step of steps) {
        if (step.kind === 'account') {
          if (!drain || d.mode() !== MODE.active || d.connectivity() !== CONN.online) continue
        } else if (d.connectivity() !== CONN.online) continue
        try {
          await step.run()
        } catch {
          // A step's failure is its own (it records its own state); the run goes on.
        }
      }
    }
    // One drain per BROWSER, not per tab: two tabs of the same account would
    // otherwise each send the queue head (op_key replay keeps adds exactly
    // once, but each could freeze its own deck attempt). A tab that finds the
    // lock taken skips this run; the holder drains.
    const locks = typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: { request: (n: string, o: { ifAvailable: boolean }, cb: (l: unknown) => Promise<void>) => Promise<void> } }).locks
      : undefined
    if (locks?.request) await locks.request(SYNC_LOCK, { ifAvailable: true }, async (lock) => { if (lock) await runSteps() })
    else await runSteps()
  } finally {
    refreshingInRun = false
    const rerun = core.end()
    running = null
    for (const l of [...runListeners]) l()
    if (rerun) requestSync(TRIGGER.rerun)
    armTick()
  }
}

/** The 60 s tick, while pending work exists or a session refresh is pending. */
export function armTick(): void {
  const d = deps
  if (!d) return
  const needed = d.hasPendingWork() || d.mode() === MODE.refreshPending
  if (!needed) {
    if (tickTimer) clearInterval(tickTimer)
    tickTimer = null
    return
  }
  if (tickTimer) return
  tickTimer = setInterval(() => {
    requestSync(TRIGGER.tick)
    const still = !!deps && (deps.hasPendingWork() || deps.mode() === MODE.refreshPending)
    if (!still && tickTimer) {
      clearInterval(tickTimer)
      tickTimer = null
    }
  }, TICK_MS)
}

/** Test seam. */
export function __resetSchedulerForTests(): void {
  if (tickTimer) clearInterval(tickTimer)
  tickTimer = null
  core = new SchedulerCore()
  deps = null
  steps = []
  running = null
  refreshingInRun = false
  planLog.length = 0
  runListeners.clear()
}
